// deps
import React, { memo, useCallback } from "react";

// utils

import { getDisplayAmount } from "./splitBillHelpers";
import { roundMoney2 } from "@dineout-payment-success-ui/utils/splitBillMoney";
import { format } from "@swiggy-private/core-ui";

// interfaces
import { SplitMethod, Contact, GuestSplitsMap } from "@dineout-payment-success-ui/interfaces";

// constants
import {
    MAX_PERCENTAGE_VALUE,
    MIN_SHARE_VALUE,
    YOU_LABEL,
    RUPEE_SYMBOL,
    PERCENTAGE_SYMBOL,
    SELF_IDENTIFIER,
} from "@dineout-payment-success-ui/constants";
import { INR_CURRENCY_SYMBOL_TEXT } from "@dineout-payment-success-ui/constants/Constants";

// components
import ContactListItem from "../ContactListItem";

interface SplitBillContactListRowProps {
    contact: Contact;
    guestSplits: GuestSplitsMap;
    selectedTab: SplitMethod;
    billAmount: number;
    allContacts: Contact[];
    onPercentageChange: (phoneNumber: string, value: string) => void;
    onAmountChange: (phoneNumber: string, value: string) => void;
    onInputBlur: () => void;
    onShareIncrement: (phoneNumber: string) => void;
    onShareDecrement: (phoneNumber: string) => void;
}

const SplitBillContactListRow: React.FC<SplitBillContactListRowProps> = ({
    contact,
    guestSplits,
    selectedTab,
    billAmount,
    allContacts,
    onPercentageChange,
    onAmountChange,
    onInputBlur,
    onShareIncrement,
    onShareDecrement,
}) => {
    const displayAmount = getDisplayAmount(contact.number, guestSplits, selectedTab, billAmount, allContacts);
    const split = guestSplits[contact.number];
    const isSelf = contact.number === SELF_IDENTIFIER;
    const name = isSelf ? format(YOU_LABEL, contact.name) : contact.name;
    const subText = format(INR_CURRENCY_SYMBOL_TEXT, displayAmount.toFixed(2));

    const handleInputChange = useCallback(
        (value: string) => {
            if (selectedTab === SplitMethod.PERCENTAGE) {
                onPercentageChange(contact.number, value);
            } else {
                onAmountChange(contact.number, value);
            }
        },
        [selectedTab, contact.number, onPercentageChange, onAmountChange],
    );

    if (!split) {
        return null;
    }

    if (selectedTab === SplitMethod.SHARE) {
        return (
            <ContactListItem
                name={name}
                showSubtext={true}
                subText={subText}
                avatar={isSelf ? undefined : contact.avatar}
                splitType={SplitMethod.SHARE}
                buttonValue={split.share ?? MIN_SHARE_VALUE}
                onIncrement={() => onShareIncrement(contact.number)}
                onDecrement={() => onShareDecrement(contact.number)}
            />
        );
    }

    const inputValue =
        selectedTab === SplitMethod.PERCENTAGE ? roundMoney2(split.percentage ?? 0) : roundMoney2(split.amount ?? 0);
    const inputSymbol = selectedTab === SplitMethod.PERCENTAGE ? PERCENTAGE_SYMBOL : RUPEE_SYMBOL;
    const showSubtext = selectedTab !== SplitMethod.AMOUNT;
    const inputMax = selectedTab === SplitMethod.PERCENTAGE ? MAX_PERCENTAGE_VALUE : billAmount;

    return (
        <ContactListItem
            name={name}
            showSubtext={showSubtext}
            subText={subText}
            avatar={isSelf ? undefined : contact.avatar}
            splitType={selectedTab}
            inputSymbol={inputSymbol}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onInputBlur={onInputBlur}
            inputMin={MIN_SHARE_VALUE}
            inputMax={inputMax}
            inputStep={0.01}
        />
    );
};

export default memo(SplitBillContactListRow);
